// frontend/src/Login.js
import React, { useState } from "react";
import api from "./api/client";

/**
 * Admin login component.
 *
 * Props:
 *  - onLogin(token) : optional callback provided by AppWrapper (your current AppWrapper passes handleAdminLogin)
 *
 * Behavior:
 *  - Calls POST /api/admin/login with { username, password }
 *  - On success: calls onLogin(token) if provided; otherwise saves admin-token to localStorage and redirects to /add-product
 */
const Login = ({ onLogin, title = "Admin Login", subtitle = "Sign in to continue" }) => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      localStorage.removeItem("admin-token");
      localStorage.removeItem("seller");
      localStorage.removeItem("seller-token");
      localStorage.removeItem("customer");
      localStorage.removeItem("customer-token");
      localStorage.removeItem("customer-id");
      localStorage.removeItem("customer-name");

      // Send request
      const res = await api.post("/api/admin/login", {
        username: form.username.trim(),
        password: form.password,
      });

      const token = res?.data?.token;
      if (!token) {
        throw new Error(res?.data?.error || "Login failed: no token returned");
      }

      // If parent provided onLogin (AppWrapper), call it so AppWrapper handles storage/navigation
      if (typeof onLogin === "function") {
        onLogin(token);
      } else {
        // Fallback: save token and redirect to admin area
        localStorage.setItem("admin-token", token);
        window.location.href = "/add-product";
      }
    } catch (err) {
      console.error("Admin login error:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Invalid credentials";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ maxWidth: 420, margin: "48px auto", padding: 16 }}>
      <h2 style={{ marginBottom: 12 }}>{title}</h2>
      <p style={{ marginTop: 0, color: "#64748b" }}>{subtitle}</p>

      {error && (
        <div style={{ color: "#b91c1c", marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <label style={{ display: "block", marginBottom: 6 }}>Username</label>
        <input
          name="username"
          value={form.username}
          onChange={onChange}
          placeholder="admin username"
          required
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", marginBottom: 6 }}>Password</label>
        <input
          name="password"
          type="password"
          value={form.password}
          onChange={onChange}
          placeholder="password"
          required
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: "100%",
          padding: 10,
          background: "#2b6cb0",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
};

export default Login;
